import {Component, ElementRef, Input, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {
  DealerSearchService,
  DealershipService,
  DealerUtilsService,
  selectActiveVehicle,
  selectFeatureFlag,
  selectProfile,
  TrackingService
} from '@ox/data';
import {AdobeDataLayerBaseCall, Brands, Dealer, Profile, UIFeatureFlag, Vehicle} from '@ox/typings';
import {AutoDestroy} from '@ox/utils';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {combineLatest, Subject, take, takeUntil, takeWhile, tap} from 'rxjs';
import {DealerMapService} from '../dealer-map/dealer-map.service';
import {preferredDealerConfig} from '../preferred-dealer/preferred-dealer.constant';
import {dealerLocatorConfig} from './locate-dealer.constant';

@Component({
  selector: 'webu-locate-dealer',
  templateUrl: './locate-dealer-webu.component.html'
})
export class LocateDealerWebuComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() prop44?: string;
  @Input() prop53?: string;
  public dealer: Dealer;
  public profile: Profile;
  public profileAddress: string;
  public activeVehicle: Vehicle;
  public brandsLov: string[] = ['Cadillac', 'Buick', 'Chevrolet', 'GMC'];
  public currentBrand: Brands = window.gm.bml.brand;
  public vehicleMake: string;
  public activeVehicle$ = this.store.select(selectActiveVehicle).pipe(
    takeUntil(this.destroy$),
    tap((vehicle) => {
      if (vehicle) {
        this.activeVehicle = {...vehicle};
        this.vehicleMake = vehicle.make;
      }
    }));
  public searching = false;
  public mapElement: HTMLElement;
  public searchResultsElement: HTMLElement;
  public modalElement: HTMLElement;
  public language = window.gm.bml?.lang;
  public isFirefox = navigator.userAgent.indexOf('Firefox') > 0;
  public displayMode = '';
  public isGuestMode = true;
  public profile$ = this.store.select(selectProfile)
    .pipe(
      takeUntil(this.destroy$),
      tap((profile) => {
        if (profile && profile.profileId) {
          this.profile = profile;
          this.profileAddress = this.dealerUtils.geocodeAddress(profile.address1, profile.city, profile.stateProvince, profile.postalCode, profile.address2);
          this.isGuestMode = false;
        } else {
          this.isGuestMode = true;
        }
      }));
  public showSearchBar = true;
  public showChangePreferredButton = true;
  public dealership = {
    current: {},
    preferred: {
      change: {
        isShown: false,
        showButton: true,
        status: preferredDealerConfig.change.status.pending
      }
    },
    show: this.showDealerInformation
  };
  public map: any;
  public preferredDealer: Dealer;
  preferredDealer$ = combineLatest([this.profile$, this.activeVehicle$]).pipe(
    tap(() => {
      if (this.profile && this.activeVehicle) this.loadPreferredDealer();
    })
  );
  public search: any = {
    dealers: [],
    dealersTooFar: false,
    invalidSearch: false,
    serverError: false,
    term: '',
    searching: false
  };

  private dataLayerV2Enabled$ = this.store.select(selectFeatureFlag({id: UIFeatureFlag.DATALAYER_V2, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true)
    );

  constructor(
    private trackingService: TrackingService,
    public modalRef: BsModalRef,
    private element: ElementRef,
    private store: Store,
    private dealerSearchService: DealerSearchService,
    private dealerMapService: DealerMapService,
    private dealerUtils: DealerUtilsService,
    private dealershipService: DealershipService
  ) {
  }

  ngOnInit(): void {
    this.mapElement = this.element.nativeElement.querySelector('.js-dealer-map-canvas');
    this.searchResultsElement = this.element.nativeElement.querySelector('.js-dealer-info-pane');
    this.modalElement = this.element.nativeElement.querySelector('.js-dealer-modal');
    this.profile$.subscribe();
    this.activeVehicle$.subscribe();
    this.preferredDealer$.subscribe();
  }

  loadPreferredDealer() {
    this.dealershipService.preferredDealer$(this.activeVehicle)
      .pipe(take(1))
      .subscribe({
        next: (response: Dealer) => {
          this.preferredDealer = (response && response.id) ? response : {} as Dealer;
        }
      });
  }

  searchDealerships(searchTerm: string) {
    this.map = this.dealerMapService.DealerMap(this.mapElement);
    const searchResults: any = {}; // give type for results

    const onFailure = (error: any) => {
      //invalid search
      if (error.status === 400) {
        this.search.invalidSearch = true;
        return;
      }

      if (error.status === 'dealersTooFar') {
        this.search.dealersTooFar = true;
        return;
      }

      this.search.searching = false;
      this.search.serverError = true;
    };

    const cleanUp = () => {
      this.onSearchEnd(searchResults.dealers);
    };

    if (!searchTerm) return;
    this.search.searching = true;
    this.displayMode = '';
    this.showSearchBar = true;

    searchTerm = searchTerm.toUpperCase();
    this.search.term = searchTerm;

    this.dealerSearchService.dealerSearchResult$(searchTerm, this.activeVehicle, this.currentBrand)
      .pipe(take(1))
      .subscribe({
        next: (dealers) => {
          if (!dealers) {
            onFailure({status: 'dealersTooFar'});
            throw new Error();
          }

          for (const [key, value] of Object.entries(dealers)) {
            this.dealerUtils.formatDealer(value);
          }
          searchResults.dealers = dealers;
          this.search.dealers = dealers;
          this.trackSearch();
        },
        error: (error) => {
          onFailure(error);
        },
        complete: () => cleanUp()
      });
  }

  onSearchEnd(searchResults: any) {
    const mapMarkers: any = [];
    let rank = 1;
    this.search.dealers = searchResults;
    this.search.searching = false;
    this.search.dealersTooFar = false;
    this.search.invalidSearch = false;
    this.search.serverError = false;

    if (!searchResults || searchResults.serverError) {
      this.search.serverError = true;
      this.trackingService.trackEvent('event25', 'locateDealer.error.serverError');
      return;
    }

    if (searchResults.dealersTooFar) {
      this.search.dealersTooFar = true;
      this.trackingService.trackEvent('event25', 'locateDealer.error.dealersTooFar');
      return;
    }

    if (searchResults.invalidSearch) {
      this.search.invalidSearch = true;
      this.trackingService.trackEvent('event20', 'locateDealer.error.invalidZipOrCityState');
      return;
    }


    const cleanUp = async () => {
      this.displayMode = 'searchResults';
      this.showSearchBar = true;
      this.map.resizeMap().pipe(take(1)).subscribe({
        complete: async () => await this.map.zoomMap(dealerLocatorConfig.map.zoomLevel)
      });

    };

    const onMapInit = () => {
      // Reset the map before adding markers
      this.map.resetMap();
      // Create the map markers
      searchResults.forEach((dealer: any) => {
        // Gives each dealer a numerical rank and marker icon based on distance (closest first)
        dealer.rank = rank;
        dealer.markerIcon = dealerLocatorConfig.map.rankIconUrlPattern.replace('{rank}', rank.toString());
        // Determine if this is a preferred dealer
        dealer.preferredDealer = false;
        if (!this.isGuestMode && this.preferredDealer) {
          dealer.preferredDealer = dealer.id === this.preferredDealer.id;
        }
        // Create the map marker information
        const markerInfo = this.dealerMapService.DealerMarkerInfo();
        if(dealer.latitude && dealer.longitude) {
          markerInfo.name = dealer.name;
          markerInfo.address = dealer.address;
          markerInfo.iconUrl = dealer.markerIcon;
          markerInfo.setPosition(dealer.latitude, dealer.longitude);
          // Add the marker and ignore any rejects/errors, just continue on...
          mapMarkers.push(this.map.addMapMarker(markerInfo));
        }

        rank++;
      });
      cleanUp();
    };

    const onMapInitFailed = (error: any) => {
      console.warn('Dealer Locator: Failed to initialize the map. ', error);
    };

    this.map.initializeMap().subscribe({
      next: () => {
        onMapInit();
      },
      error: (err: unknown) => onMapInitFailed(err)
    });
  }

  showSearchResults() {
    this.displayMode = 'searchResults';
    this.showSearchBar = true;
  }

  receiveDealer($event: Dealer) {
    this.dealer = $event;
  }

  changeState($event: string) {
    this.trackSearch($event);
    if ($event === 'viewDealerInfo') {
      this.showDealerInformation(this.dealer);
    } else if ($event === 'directions') {
      this.showDirections(this.dealer);
    } else if ($event === 'goToPreferredDealer') {
      this.showChangePreferredDealer(this.dealer);
    } else if ($event === 'cancel') {
      this.onChangePreferredDealerCancelled();
    } else if ($event === 'save') {
      this.onChangePreferredDealerSaved();
    }
  }

  showDealerInformation(dealer?: any) {
    if (!dealer || !dealer.id) return;
    this.displayMode = 'dealerInfo';
    this.showSearchBar = false;
  }

  showChangePreferredDealer(dealer: any) {
    if (!dealer || !dealer.id) return;
    this.dealership.preferred.change.status = preferredDealerConfig.change.status.pending;
    this.dealership.preferred.change.isShown = true;
  }

  onChangePreferredDealerCancelled() {
    this.dealership.preferred.change.isShown = false;
    this.dealership.preferred.change.status = preferredDealerConfig.change.status.pending;
  }

  onChangePreferredDealerSaved() {
    this.activeVehicle.preferredDealer = this.dealer;
    this.dealershipService.setPreferredDealer(this.dealer, this.activeVehicle)
      .pipe(take(1))
      .subscribe({
        next: () => {
          setTimeout(() => {
            this.dealership.preferred.change.isShown = false;
            this.dealership.preferred.change.status = preferredDealerConfig.change.status.success;
          }, 2000);
        },
        error: () => {
          this.dealership.preferred.change.isShown = false;
          this.dealership.preferred.change.status = preferredDealerConfig.change.status.failure;
        }
      });

    setTimeout(() => {
      this.dealership.preferred.change.status = preferredDealerConfig.change.status.pending;
    }, preferredDealerConfig.change.successMessageTimeout);
  }

  showDirections(dealer?: any) {
    if (!dealer || !dealer.id) return;
    this.displayMode = 'directions';
    this.showSearchBar = false;
  }

  hideDirections() {
    this.displayMode = 'searchResults';
    this.showSearchBar = true;
  }

  hideSearchResults() {
    this.displayMode = '';
    this.showSearchBar = true;
  }

  trackSearch(selectedItem: string | undefined = undefined) {
    this.dataLayerV2Enabled$.subscribe((dataLayerV2Enabled) => {
      if (dataLayerV2Enabled === 'true') {
        const search = {
          search : {
            term: this.search.term,
            resultCount: this.search.dealers.length,
            resultClicked: selectedItem,
            facetsUsed: [this.currentBrand],
            filtersUsed: []
          }
        };
        AdobeDataLayerBaseCall.setGmUnifiedProperty(search, window);
      }
    });
  }
}
